import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Markets.css';
import { useConfig } from '../ConfigContext';

// ─── Static market data ───────────────────────────────────────
// Each market has a hero tagline, a set of use cases, and a
// deployment note. Content can be extended via config later.

const MARKETS = [
  {
    id: 'defense',
    label: 'Defense & Government',
    img: '/icons/landing/defense.png',
    tagline: 'Mission-critical voice detection in air-gapped, classified, and contested environments.',
    icon: '⬡',
    accentClass: 'mk-accent-blue',
    useCases: [
      {
        title: 'Callsign & Designator Detection',
        desc: 'Detect specific callsigns, unit designators, and operational codewords in live radio streams — without transmitting audio to any external server. Runs fully on-device in air-gapped environments.',
        tags: ['On-Device', 'Air-Gapped', 'Pryme or Apex'],
      },
      {
        title: 'Operational Command Recognition',
        desc: 'Recognize predefined command vocabulary — execute, abort, hold, go — with low-latency detection on embedded hardware. No cloud dependency at runtime.',
        tags: ['Edge Hardware', 'Low Latency', 'Pryme'],
      },
      {
        title: 'Classified Terminology Transcription',
        desc: 'Transcribe briefings, debriefs, and field reports with accurate rendering of classified terminology, acronyms, and unit names — calibrated to your vocabulary, running on your infrastructure.',
        tags: ['On-Premise', 'Apex', 'No Audio Retained'],
      },
      {
        title: 'Multilingual Field Support',
        desc: 'Detect keywords and transcribe speech across languages without separate model deployments. Same SDK, same API, configured at runtime.',
        tags: ['Multilingual', 'Runtime Config', 'Pryme or Apex'],
      },
    ],
    deployment: 'Air-gapped on-device deployment. No audio ever leaves the hardware. Compatible with classified and disconnected environments.',
    product: 'Apex',
    productLink: '/apex',
  },
  {
    id: 'healthcare',
    label: 'Healthcare',
    img: '/icons/landing/healthcare.png',
    tagline: 'Accurate detection and transcription of clinical terminology — on your infrastructure, with no audio leaving your network.',
    icon: '⬡',
    accentClass: 'mk-accent-teal',
    useCases: [
      {
        title: 'Drug Name & Dosage Detection',
        desc: 'Detect spoken drug names, dosage instructions, and procedure codes in clinical settings. Apex calibrates to the exact terminology your facility uses — including brand names and specialist abbreviations that general ASR consistently mis-renders.',
        tags: ['Apex', 'Domain Vocabulary', 'On-Premise'],
      },
      {
        title: 'Physician Voice Documentation',
        desc: 'Transcribe physician notes, dictations, and patient interactions with vocabulary calibrated to your specialty. No audio sent to third-party cloud providers.',
        tags: ['Transcription', 'Apex', 'HIPAA-Aligned'],
      },
      {
        title: 'Alert & Trigger Detection',
        desc: 'Monitor live audio streams for specific clinical phrases — code blue, stat, patient fall — and trigger downstream workflows without requiring a full transcription pipeline.',
        tags: ['Real-Time', 'Pryme', 'Workflow Automation'],
      },
      {
        title: 'Accessibility & Patient Interfaces',
        desc: 'Voice-enable patient-facing devices and interfaces for diverse speakers and accents — no per-patient audio samples required. Detection is live from the first utterance.',
        tags: ['Accessibility', 'Pryme', 'No Training Data'],
      },
    ],
    deployment: 'On-premise or private cloud deployment. Audio stays within your network boundary. No Tytonvoice servers in the data path.',
    product: 'Apex',
    productLink: '/apex',
  },
  {
    id: 'iot',
    label: 'Smart Devices & IoT',
    img: '/icons/landing/iot.png',
    tagline: 'Custom wake words and voice commands on embedded hardware — deploy the same day, no vendor lock-in.',
    icon: '⬡',
    accentClass: 'mk-accent-blue',
    useCases: [
      {
        title: 'Custom Wake Word Detection',
        desc: 'Activate any device with any brand-specific wake phrase. Define it in text and deploy the same day — no audio samples, no managed training pipeline, no per-device licensing fees.',
        tags: ['Pryme', 'No Training Data', 'Edge-Ready'],
      },
      {
        title: 'Multi-Keyword Command Vocabulary',
        desc: 'Monitor a live microphone stream for multiple commands simultaneously. Start, stop, adjust, confirm — all evaluated in parallel in a single inference pass on CPU hardware.',
        tags: ['Multi-Keyword', 'CPU Inference', 'Pryme'],
      },
      {
        title: 'Offline Device Operation',
        desc: 'Full VAD, detection, and transcription pipeline running on-chip with no network dependency. Proven on Raspberry Pi 4 at under 50ms end-to-end detection latency.',
        tags: ['OFFLINE=true', 'Raspberry Pi', 'Sub-50ms'],
      },
      {
        title: 'Runtime Vocabulary Updates',
        desc: 'Change wake words and command vocabulary at runtime without redeploying firmware. New labels take effect immediately — no retraining cycle, no downtime.',
        tags: ['Runtime Config', 'No Redeployment', 'Pryme'],
      },
    ],
    deployment: 'Runs fully on-device. Raspberry Pi, Android, Linux, Windows. Same SDK API whether offline or cloud-connected.',
    product: 'Pryme',
    productLink: '/pryme',
  },
  {
    id: 'robotics',
    label: 'Robotics & Automation',
    img: '/icons/landing/robot.png',
    tagline: 'Precise voice command detection for safety-critical automated systems — high confidence, noise robust, CPU-only.',
    icon: '⬡',
    accentClass: 'mk-accent-teal',
    useCases: [
      {
        title: 'Safety Command Detection',
        desc: 'Detect stop, abort, emergency, and hold commands with high confidence in noisy industrial environments. Neural VAD filters ambient noise before the detection layer — reducing false positives without sacrificing sensitivity.',
        tags: ['Safety-Critical', 'Neural VAD', 'Pryme or Apex'],
      },
      {
        title: 'Session Boundary Control',
        desc: 'Use wake word and shutdown word detection to cleanly open and close voice command sessions. Prevents unintended activations and provides clear session semantics for control systems.',
        tags: ['Wake + Shutdown', 'Session Control', 'Pryme'],
      },
      {
        title: 'Operator Instruction Recognition',
        desc: 'Recognize a predefined vocabulary of operator instructions across multiple speakers on the production floor — without per-operator enrollment or audio samples.',
        tags: ['Multi-Speaker', 'No Enrollment', 'Apex'],
      },
      {
        title: 'Embedded Edge Deployment',
        desc: 'Deploy the full detection pipeline on the robot\'s onboard compute — no cloud inference, no network latency, no single point of failure at the infrastructure level.',
        tags: ['On-Device', 'No Network Dependency', 'Edge'],
      },
    ],
    deployment: 'CPU-optimized inference. Runs on embedded Linux, ARM64, and x86_64 hardware. No GPU required.',
    product: 'Apex',
    productLink: '/apex',
  },
  {
    id: 'enterprise',
    label: 'Enterprise & Contact Centers',
    img: '/icons/landing/enterprise.png',
    tagline: 'Real-time keyword monitoring and accurate transcription across live interactions — no custom model per use case.',
    icon: '⬡',
    accentClass: 'mk-accent-blue',
    useCases: [
      {
        title: 'Compliance Keyword Monitoring',
        desc: 'Monitor live agent-customer interactions for regulatory triggers, required disclosures, and prohibited phrases in real time. Define new compliance keywords in text and deploy immediately — no retraining cycle.',
        tags: ['Real-Time', 'Compliance', 'Pryme'],
      },
      {
        title: 'Proprietary Terminology Transcription',
        desc: 'Transcribe internal product names, legal terms, and domain jargon accurately — calibrated to your vocabulary. Stop losing precision to general-purpose ASR that consistently mis-renders your specific terms.',
        tags: ['Domain Vocabulary', 'Apex', 'Transcription'],
      },
      {
        title: 'Intent & Escalation Detection',
        desc: 'Detect escalation signals, competitor mentions, and cancellation intent in live calls without building a separate model per detection category. Define new intents and monitor immediately.',
        tags: ['Intent Detection', 'Multi-Label', 'Pryme'],
      },
      {
        title: 'Internal Voice Interfaces',
        desc: 'Voice-enable internal tools and workflows — meeting assistants, documentation dictation, command-and-control interfaces — with runtime vocabulary that updates without redeployment.',
        tags: ['Internal Tools', 'Runtime Updates', 'Pryme or Apex'],
      },
    ],
    deployment: 'Cloud backend on your infrastructure. REST API and WebSocket config. Clients on web, Android, Windows, and Linux.',
    product: 'Pryme',
    productLink: '/pryme',
  },
  {
    id: 'accessibility',
    label: 'Accessibility',
    img: '/icons/landing/accessibility.png',
    tagline: 'Voice interfaces that work immediately for every speaker — no per-user samples, no enrollment pipeline.',
    icon: '⬡',
    accentClass: 'mk-accent-teal',
    useCases: [
      {
        title: 'Universal Voice Control',
        desc: 'Build voice interfaces that work immediately for diverse speakers, accents, dialects, and speech patterns. No per-user audio samples. No enrollment session. Detection is live from the first utterance.',
        tags: ['No Enrollment', 'Any Speaker', 'Pryme'],
      },
      {
        title: 'Assistive Device Activation',
        desc: 'Activate assistive devices and accessibility tools with custom voice commands — tailored to the user\'s vocabulary without collecting training data from the user.',
        tags: ['Custom Commands', 'Privacy-First', 'Pryme'],
      },
      {
        title: 'Augmentative Communication Support',
        desc: 'Integrate voice detection into AAC devices and communication aids. Define the detection vocabulary that matches each user\'s communication patterns — updated at runtime as needs change.',
        tags: ['AAC', 'Runtime Vocab', 'Edge or Cloud'],
      },
      {
        title: 'Low-Latency Response Interfaces',
        desc: 'Voice interfaces for users who rely on fast, predictable system response. Sub-50ms detection latency on CPU hardware means the device responds at human conversational speed.',
        tags: ['Sub-50ms', 'CPU Inference', 'On-Device'],
      },
    ],
    deployment: 'Edge or cloud. No audio samples required from end users. Works across diverse speaker profiles out of the box.',
    product: 'Pryme',
    productLink: '/pryme',
  },
];

// ─────────────────────────────────────────────────────────────
function Markets() {
  const config = useConfig();
  const [activeMarket, setActiveMarket] = useState(MARKETS[0].id);
  const current = MARKETS.find(m => m.id === activeMarket);

  return (
    <div className="mk-page">

      {/* ── Hero ── */}
      <section className="mk-hero">
        <div className="mk-hero-inner">
          <div className="mk-hero-badge">Markets</div>
          <h1 className="mk-hero-title">
            Voice intelligence for<br />
            <span className="mk-hero-accent">every deployment context</span>
          </h1>
          <p className="mk-hero-subtitle">
            From air-gapped defense hardware to patient-facing clinical tools —
            {' '}{config.branding?.productName ?? 'Tytonvoice'} adapts to the vocabulary,
            infrastructure, and privacy requirements of each environment.
            No retraining. Same SDK.
          </p>
          <div className="mk-hero-cta">
            <a
              href={config.navbar?.contactUs?.url ?? '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="mk-btn-primary"
            >
              Contact Us
            </a>
            <Link to="/pryme" className="mk-btn-secondary">See Pryme</Link>
          </div>
        </div>
      </section>

      {/* ── Market explorer ── */}
      <section className="mk-explorer">
        <div className="mk-explorer-inner">

          {/* Sidebar tabs */}
          <nav className="mk-sidebar">
            {MARKETS.map(m => (
              <button
                key={m.id}
                className={`mk-sidebar-item ${activeMarket === m.id ? 'mk-sidebar-active' : ''}`}
                onClick={() => setActiveMarket(m.id)}
              >
                {m.img
                  ? <img src={m.img} alt={m.label} className="mk-sidebar-img" />
                  : <span className={`mk-sidebar-icon ${m.accentClass}`}>{m.icon}</span>
                }
                <span className="mk-sidebar-label">{m.label}</span>
              </button>
            ))}
          </nav>

          {/* Market detail panel */}
          <div className="mk-panel">
            <div className="mk-panel-header">
              <h2 className="mk-panel-title">{current.label}</h2>
              <p className="mk-panel-tagline">{current.tagline}</p>
            </div>

            <div className="mk-use-cases">
              {current.useCases.map((uc, i) => (
                <div key={i} className="mk-uc-card">
                  <h3 className="mk-uc-title">{uc.title}</h3>
                  <p className="mk-uc-desc">{uc.desc}</p>
                  <div className="mk-uc-tags">
                    {uc.tags.map(tag => (
                      <span key={tag} className="mk-uc-tag">{tag}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mk-panel-footer">
              <div className="mk-deployment">
                <span className="mk-deployment-label">Typical deployment</span>
                <p className="mk-deployment-text">{current.deployment}</p>
              </div>
              <Link to={current.productLink} className="mk-panel-cta">
                Explore {current.product} →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Bottom CTA band ── */}
      <section className="mk-cta-band">
        <div className="mk-cta-inner">
          <h2 className="mk-cta-heading">Deploy voice detection today — no training data required.</h2>
          <p className="mk-cta-sub">
            Define your keywords in plain text. Start detecting in minutes.
          </p>
          <a
            href={config.navbar?.contactUs?.url ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="mk-btn-primary"
          >
            Contact Us
          </a>
        </div>
      </section>

    </div>
  );
}

export default Markets;